import { useState, useEffect, useRef, useCallback } from "react";

const API_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/youtube-search`;
const API_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const fetchApi = async (params: Record<string, string>, signal?: AbortSignal) => {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API_BASE}?${qs}`, {
    headers: { "Content-Type": "application/json", apikey: API_KEY },
    signal,
  });
  return res.json();
};

// ----- Types -----
export interface YtChannel {
  id: string;
  name: string;
  handle: string;
  url: string;
  thumb: string;
  subscribers: number;
  verified: boolean;
  description: string;
  banner?: string;
  totalViews?: number;
}

export interface YtVideo {
  id: string;
  title: string;
  author?: string;
  authorId?: string;
  url: string;
  thumb: string;
  views: number;
  likes?: number;
  duration: number;
  published?: string;
  isShort?: boolean;
  description?: string;
}

// ----- Search (debounced, channels + videos) -----
export function useYtSearch(query: string) {
  const [channels, setChannels] = useState<YtChannel[]>([]);
  const [videos, setVideos] = useState<YtVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [stale, setStale] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setChannels([]); setVideos([]); setSearched(false); setLoading(false); setStale(false);
      return;
    }
    setLoading(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    debounceRef.current = setTimeout(async () => {
      try {
        const json = await fetchApi({ action: "search", q: trimmed }, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setChannels(Array.isArray(json?.channels) ? json.channels : []);
        setVideos(Array.isArray(json?.videos) ? json.videos : []);
        setStale(Boolean(json?._stale));
        setSearched(true);
      } catch {
        if (!ctrl.signal.aborted) { setChannels([]); setVideos([]); setSearched(true); setStale(false); }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      ctrl.abort();
    };
  }, [query]);

  return { channels, videos, loading, searched, stale };
}

// ----- Channel detail + recent videos -----
export function useYtChannel(channelId: string) {
  const [channel, setChannel] = useState<YtChannel | null>(null);
  const [videos, setVideos] = useState<YtVideo[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!channelId) return;
    const ctrl = new AbortController();
    setLoading(true);
    fetchApi({ action: "channel", id: channelId }, ctrl.signal)
      .then((j) => {
        if (ctrl.signal.aborted) return;
        setChannel(j?.channel || null);
        setVideos(Array.isArray(j?.videos) ? j.videos : []);
      })
      .catch(() => {})
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [channelId]);

  return { channel, videos, loading };
}

// ----- Video detail -----
export function useYtVideo(videoId: string) {
  const [video, setVideo] = useState<YtVideo | null>(null);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async (id: string, signal?: AbortSignal) => {
    if (!id) return;
    setLoading(true);
    try {
      const j = await fetchApi({ action: "video", id }, signal);
      if (signal?.aborted) return;
      setVideo(j?.video || null);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!videoId) return;
    const ctrl = new AbortController();
    refetch(videoId, ctrl.signal);
    return () => ctrl.abort();
  }, [videoId, refetch]);

  return { video, loading, refetch };
}

// Parse a YouTube URL for video ID or channel ID
export function parseYouTubeUrl(input: string): { kind: "video" | "channel" | "handle"; id: string } | null {
  if (!input) return null;
  const s = input.trim();
  // Video: watch?v= / youtu.be/ / shorts/
  let m = s.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/);
  if (m) return { kind: "video", id: m[1] };
  // Channel ID: /channel/UCxxxx
  m = s.match(/youtube\.com\/channel\/([A-Za-z0-9_-]+)/);
  if (m) return { kind: "channel", id: m[1] };
  // Handle: /@handle
  m = s.match(/youtube\.com\/@([A-Za-z0-9._-]+)/);
  if (m) return { kind: "handle", id: m[1] };
  // Bare 11-char ID
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return { kind: "video", id: s };
  return null;
}
